/* eslint-disable max-lines */
import * as React from "react";
import { connect } from "react-redux";
import { values, flatten } from "lodash";
import { Checkbox, Container, Divider } from "semantic-ui-react";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";

import { AccordionQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireContainer";
import { AnsweredQuestion, AnsweredQuestionWithId, LinkObject } from "@teamprevent/common/model";
import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { CheckboxQuestion, Checked } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { updateQuestion, removeQuestion, submitInvalidQuestionnaire } from "@teamprevent/common/questionnaires/actions";
import { CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig/models";
import { ImmunisationTaskType } from "@teamprevent/common/consultations/models";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { QuestionAnswer } from "@teamprevent/common/questionnaires/model";
import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";
import { QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { ApplicationState } from "../../applicationState";
import { ConfidentialitySection } from "../../consultations/components/ConfidentialitySection";
import { DataProtectionSection } from "../../consultations/components/DataProtectionSection";
import { OhCase } from "../../crud/ohCase";
import { routeIdSelector as customerIdSelector } from "../../crud/customer/selectors";
import { ohCaseSelector, routeIdSelector as caseIdSelector } from "../../crud/ohCase/selectors";
import { employeeRecordSelector } from "../../crud/employeeRecord/selectors";
import { ImmunisationTaskModel, mapToFollowUpTask, TaskType } from "../../task/model";
import { ImmunisationTypeTasksSelector } from "../../vaccinationConsultation/components/ImmunisationTypeTasksSelector";
import { EmployeeRecord } from "../../crud/employeeRecord";
import { DidNotAttendButton } from "../../clinicalNotes/components/DidNotAttendButton";
import { loadOhCaseDetail } from "../../crud/ohCase/actions";
import { ReviewImmunisationBloodTestResultTask } from "../../task/taskTypes";
import { loadClinicalNotes } from "../actions";
import { TasksApi } from "../../tasks/tasksApi";
import { AddTasksInputs } from "../../tasks/components/addTasksInputs";
import { FollowUpTask } from "../../immunisationReview/components/FollowUpTasks";
import { AddNextTaskFormModel } from "../../tasks/models";
import { ImmunisationTypeTask } from "../../crud/immunisationTypeTask";
import { ImmunisationType } from "../../crud/immunisationType";
import { immunisationTypeTasksSelector } from "../../crud/immunisationTypeTask/selectors";
import { activeImmunisationTypesSelector } from "../../crud/immunisationType/selectors";

import { BloodTestConsultationOld } from "./BloodTestConsultationOld";

interface StateProps {
    customerId: string;
    taskId: string;
    task: ImmunisationTaskModel;
    ohCaseId: string;
    ohCase: OhCase;
    readOnly: boolean;
    questions: AnsweredQuestionWithId[];
    employeeRecord: EmployeeRecord;
    bloodTestsConsentSectionIndex: number;
    ohCaseAssignableTaskTypes: TaskType[];
    immunisationTypeTasks: ImmunisationTypeTask[];
    immunisationTypes: ImmunisationType[];
}

interface DispatchProps {
    loadOhCaseDetail: (ohCaseId: string, customerId: string) => void;
    loadQuestionnaire: (ohCaseId: string, taskId: string) => void;
    submitInvalidQuestionnaire: () => void;
    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion }) => void;
    removeQuestion: (payload: { sectionId: string; id: string }) => void;
}

interface OwnProps {
    submitQuestionnaire: (questions: AnsweredQuestionWithId[]) => void;
    tasksSection: JSX.Element;
    onImmunisationTypesChanged?: (immunisationTypeTaskIds: string[], noBloodTestsTakenToday: boolean) => void;
    tasksIsValid?: boolean;
    toggleRestrictToTaskTypes?: (taskType: string, mantouxReadingChecked: boolean) => void;
    onChangeTasks?: (tasks: FollowUpTask[]) => void;
}

interface State {
    immunisationTypeTasks: LinkObject[];
}

type Props = StateProps & DispatchProps & OwnProps;

export const QUESTIONNAIRE_KEY = "bloodTestConsultation";

const BloodTestsConsentSectionId = "BloodTestsConsent";

const ConsentObtained = "Consent obtained";

class BloodTestConsultationUnconnected extends React.Component<Props, State> {
    public readonly noBloodTestsTakenTodayQuestion = {
        id: "NoBloodTestsTakenToday",
        questionText: "No blood tests taken today",
    };

    public readonly bloodTestConsentObtainedQuestion = {
        id: "BloodTestConsentObtained",
        questionText: "I confirm that I have discussed the requirement for testing with the employee and have obtained their consent to take blood",
    };

    constructor(props: Props) {
        super(props);

        this.loadQuestionnaire = this.loadQuestionnaire.bind(this);
        this.submitQuestionnaire = this.submitQuestionnaire.bind(this);
    }

    public state: State = {
        immunisationTypeTasks: (this.props.task && this.props.task.immunisationTypeTasks) || []
    };

    private onImmunistionTypeTasksChanged = (immunisationTypeTasks: LinkObject[]) => {
        this.setState({ immunisationTypeTasks });
        if (this.props.onImmunisationTypesChanged) {
            this.props.onImmunisationTypesChanged(
                immunisationTypeTasks.filter(t => t).map(t => t.id),
                this.getAnswerToQuestionWithId(this.noBloodTestsTakenTodayQuestion.id) === Checked
            );
        }

        if (immunisationTypeTasks.length === 0 && !this.props.readOnly) {
            this.props.updateAnswer({
                sectionId: "ImmunisationTypeTasks",
                id: this.noBloodTestsTakenTodayQuestion.id,
                question: { question: this.noBloodTestsTakenTodayQuestion.questionText, answer: Checked, isValid: true }
            });
        }
    };

    public componentDidMount = () => {
        if (!this.props.ohCase || !this.props.ohCase.id) {
            this.props.loadOhCaseDetail(this.props.ohCaseId, this.props.customerId);
        }
    };

    public componentDidUpdate = (prevProps: StateProps) => {
        if ((!prevProps.task || !prevProps.task.immunisationTypeTasks) && this.props.task && this.props.task.immunisationTypeTasks) {
            this.setState({ immunisationTypeTasks: this.props.task.immunisationTypeTasks });
        }
    };

    private getAnswerToQuestionWithId(id: string) {
        const question = this.props.questions.find(q => q.id === id);
        return question?.answer;
    }

    private onNoBloodTestsTakenTodayChanged = (answer: QuestionAnswer) => {
        if (this.props.onImmunisationTypesChanged) {
            const noBloodTestsTakenToday = answer === Checked;
            this.props.onImmunisationTypesChanged(this.state.immunisationTypeTasks?.filter(t => t).map(t => t.id), noBloodTestsTakenToday);
        }
    };

    public updateSnippets = (snippetText: string, sectionId: string, id: string, questionText: string) => {
        const questionAnswer = this.getAnswerToQuestionWithId(id);

        const question = {
            question: questionText,
            answer: (questionAnswer ? questionAnswer + " " : "") + snippetText,
            isValid: true
        };

        this.props.updateAnswer({ sectionId, id, question });
    };

    public render() {

        const noBloodTestsTakenToday = this.getAnswerToQuestionWithId(this.noBloodTestsTakenTodayQuestion.id) === Checked;

        return (
            <Container>
                <h2>
                    Blood Test Consultation for
                    {this.props.ohCase && ` ${this.props.ohCase.employeeFirstName || ""} ${this.props.ohCase.employeeLastName || ""}`}
                </h2>
                {!this.props.readOnly && <DidNotAttendButton
                    customerId={this.props.customerId}
                    ohCaseId={this.props.ohCaseId}
                    task={this.props.task}
                />}
                <AccordionQuestionnaireContainer
                    loadQuestionnaire={this.loadQuestionnaire}
                    questionnaireKey={QUESTIONNAIRE_KEY}
                    readonly={this.props.readOnly}
                    submitQuestionnaire={this.submitQuestionnaire}
                    questionnaireType={QuestionnaireTypes.ClinicalNotes}
                    task={this.props.task}
                    removeAppointment={() => new TasksApi().removeAppointment(this.props.task.caseId, this.props.task.id)}
                >
                    {!this.props.readOnly &&
                        <AccordionQuestionnaireSection id="ImmunisationTypeTasks" title="Blood Tests" neverHide index={0}>
                            <CheckboxQuestion
                                readonly={this.props.readOnly}
                                questionConfig={({
                                    id: this.noBloodTestsTakenTodayQuestion.id,
                                    questionText: "No blood tests taken today"
                                })}
                                value="Yes"
                                outdated={{
                                    isOutdated: false,
                                    questionnaireKey: QUESTIONNAIRE_KEY,
                                    sectionId: "ImmunisationTypeTasks"
                                }}
                                onAnswerChange={this.onNoBloodTestsTakenTodayChanged}
                            />
                            {!noBloodTestsTakenToday &&
                                <ImmunisationTypeTasksSelector
                                    selectedTypes={this.state.immunisationTypeTasks}
                                    onSelectionChanged={this.onImmunistionTypeTasksChanged}
                                    addMoreButtonText="Add more blood tests"
                                    taskTypeFilter={ImmunisationTaskType.BloodTest}
                                />
                            }
                        </AccordionQuestionnaireSection>
                    }
                    <ConfidentialitySection
                        index={1}
                        ohCase={this.props.ohCase}
                        employeeRecord={this.props.employeeRecord}
                        questionnaireKey={QUESTIONNAIRE_KEY}
                        readonly={this.props.readOnly}
                    />
                    <DataProtectionSection
                        index={2}
                        questionnaireKey={QUESTIONNAIRE_KEY}
                        readonly={this.props.readOnly}
                    />
                    {!noBloodTestsTakenToday &&
                        <>
                            <AccordionQuestionnaireSection
                                index={3}
                                id={BloodTestsConsentSectionId}
                                title="Blood Tests Consent"
                            >
                                <CheckboxQuestion
                                    readonly={this.props.readOnly}
                                    mandatory
                                    addAsteriskIfMandatory
                                    questionConfig={this.bloodTestConsentObtainedQuestion}
                                    value={ConsentObtained}
                                />
                            </AccordionQuestionnaireSection>
                            {this.state.immunisationTypeTasks.map((immunisationTypeTask, index) => {
                                if (immunisationTypeTask == null) { // eslint-disable-line eqeqeq
                                    return null;
                                }

                                return (
                                    <AccordionQuestionnaireSection
                                        id={`BloodTestTask${immunisationTypeTask.id}`}
                                        title={immunisationTypeTask.name}
                                        key={immunisationTypeTask.id}
                                        index={this.props.bloodTestsConsentSectionIndex + index + 1}
                                    >
                                        <CheckboxQuestion
                                            mandatory={!noBloodTestsTakenToday}
                                            addAsteriskIfMandatory
                                            readonly={this.props.readOnly}
                                            questionConfig={({ id: `BloodTestTask${immunisationTypeTask.id}`, questionText: immunisationTypeTask.name })}
                                            value="Yes"
                                            // eslint-disable-next-line react/jsx-no-bind
                                            onAnswerChange={newValue => this.updateDependentQuestion(
                                                `BloodTestTask${immunisationTypeTask.id}`,
                                                {
                                                    id: `BloodTestTaskReason${immunisationTypeTask.id}`,
                                                    questionText: "Reason"
                                                },
                                                newValue as string)
                                            }
                                        />
                                        {(this.props.questions.find(q => q.id === `BloodTestTask${immunisationTypeTask.id}`) || { answer: null }).answer &&
                                            <DropdownQuestion
                                                readonly={this.props.readOnly}
                                                questionConfig={({ id: `BloodTestTaskReason${immunisationTypeTask.id}`, questionText: "Reason" })}
                                                mandatory
                                                options={[
                                                    "New Employee",
                                                    "Post Vaccination",
                                                    "History Unknown",
                                                    "Post Exposure Incident 6 week",
                                                    "Post Exposure Incident 12 week",
                                                    "Post Exposure Incident 24 week",
                                                    "Hep B Primary Course",
                                                    "Hep B Secondary Course",
                                                    "EPP Worker",
                                                    "Vaccine Non-Responder",
                                                    "Serum Save - Post NSI"
                                                ].map(o => ({ text: o, value: o }))}
                                            />}
                                    </AccordionQuestionnaireSection>
                                );
                            })}
                        </>
                    }
                    <AccordionQuestionnaireSection
                        title="Clinical Notes"
                        id="ClinicalNotes"
                    >
                        <TextQuestion
                            readonly={this.props.readOnly}
                            questionConfig={({
                                id: "ClinicalNotes",
                                questionText: "Clinical Notes"
                            })}
                            outdated={{
                                isOutdated: false,
                                questionnaireKey: QUESTIONNAIRE_KEY,
                                sectionId: "ClinicalNotes"
                            }}
                            textSnippetSelector={
                                <TextSnippetSelector
                                    onSelect={selectedSnippet => this.updateSnippets(
                                        selectedSnippet,
                                        "ClinicalNotes",
                                        "ClinicalNotes",
                                        "Clinical Notes"
                                    )}
                                    padded
                                />
                            }
                            multiline
                        />
                        {!noBloodTestsTakenToday ?
                            <TextQuestion
                                readonly={this.props.readOnly}
                                questionConfig={({
                                    id: "LaboratoryReference",
                                    questionText: "Laboratory Reference"
                                })}
                                outdated={{
                                    isOutdated: false,
                                    questionnaireKey: QUESTIONNAIRE_KEY,
                                    sectionId: "ClinicalNotes"
                                }}
                            /> :
                            <TextQuestion
                                readonly={this.props.readOnly}
                                questionConfig={({
                                    id: "WhyNoBloodTestsTaken",
                                    questionText: "Why no blood tests taken"
                                })}
                                mandatory={true}
                                outdated={{
                                    isOutdated: false,
                                    questionnaireKey: QUESTIONNAIRE_KEY,
                                    sectionId: "ClinicalNotes"
                                }}
                            />
                        }
                    </AccordionQuestionnaireSection>
                    {!this.props.readOnly &&
                        <AccordionQuestionnaireSection
                            id="NextTasks"
                            title="Next Tasks"
                            neverHide={true}
                            additionalIsValid={this.props.tasksIsValid}
                        >
                            {(this.props.toggleRestrictToTaskTypes && !noBloodTestsTakenToday) &&
                                <>
                                    <Checkbox
                                        label={"Mantoux Reading"}
                                        onChange={(v, d) => this.props.toggleRestrictToTaskTypes(ReviewImmunisationBloodTestResultTask.type, d.checked)}
                                    />
                                    <Divider hidden />
                                </>
                            }
                            {!noBloodTestsTakenToday ?
                                <>{this.props.tasksSection}</>
                                : <AddTasksInputs
                                    immunisationTypeTasks={this.props.immunisationTypeTasks}
                                    immunisationTypes={this.props.immunisationTypes}
                                    taskTypes={this.props.ohCaseAssignableTaskTypes}
                                    onChange={this.onChangeTasks}
                                    ohCaseId={this.props.ohCaseId}
                                    showErrors={true}
                                    assignmentOptionRequired={true}
                                />
                            }
                        </AccordionQuestionnaireSection>}
                </AccordionQuestionnaireContainer>
            </Container>
        );
    }

    private async loadQuestionnaire() {
        return this.props.loadQuestionnaire(this.props.ohCaseId, this.props.taskId);
    }

    private async submitQuestionnaire(): Promise<boolean> {
        if (this.props.questions.some(q => !q.isValid)) {
            await this.props.submitInvalidQuestionnaire();
            return false;
        }

        await this.props.submitQuestionnaire(this.props.questions);
        return true;
    }

    private updateDependentQuestion(sectionId: string, question: CustomQuestionConfig, answer: string) {
        if (answer) {
            this.props.updateAnswer({ sectionId, id: question.id, question: { question: question.questionText, id: question.id, answer: "" } });
        } else {
            this.props.removeQuestion({ sectionId, id: question.id });
        }
    }

    private onChangeTasks = (tasks: AddNextTaskFormModel[]) => {
        if (this.props.onChangeTasks) {
            const nextTasks: FollowUpTask[] = (tasks ?? []).map(t => mapToFollowUpTask(t, t.isValid));
            this.props.onChangeTasks(nextTasks);
        }
    };
}

const mapStateToProps = (state: ApplicationState): StateProps => {
    const ohCase = ohCaseSelector(state);
    const taskId = state.router.params.taskId;
    const task = (ohCase.tasks && ohCase.tasks.find(t => t.id === taskId)) as ImmunisationTaskModel;
    const questionnaireState = state.questionnaires[QUESTIONNAIRE_KEY];
    const bloodTestsConsentSectionIndex = questionnaireState?.sections.find(s => s.id === BloodTestsConsentSectionId)?.index;
    const ohCaseAssignableTaskTypes = state.tasks.taskTypes;
    return {
        customerId: customerIdSelector(state),
        ohCaseId: caseIdSelector(state),
        ohCase,
        employeeRecord: employeeRecordSelector(state),
        questions: questionnaireState ? flatten(questionnaireState.sections.map(section => values(section.questions))) : [],
        taskId,
        task,
        readOnly: !task || task.completed,
        bloodTestsConsentSectionIndex,
        ohCaseAssignableTaskTypes,
        immunisationTypeTasks: immunisationTypeTasksSelector(state),
        immunisationTypes: activeImmunisationTypesSelector(state),
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, Action>): DispatchProps => ({
    loadOhCaseDetail: (ohCaseId: string, customerId: string) => dispatch(loadOhCaseDetail({ ohCaseId, customerId })),
    loadQuestionnaire: (ohCaseId: string, taskId: string) => {
        dispatch(loadClinicalNotes(ohCaseId, taskId, QUESTIONNAIRE_KEY));
    },
    submitInvalidQuestionnaire: () => dispatch(submitInvalidQuestionnaire(QUESTIONNAIRE_KEY, true)),
    updateAnswer: payload => dispatch(updateQuestion({ ...payload, questionnaireKey: QUESTIONNAIRE_KEY })),
    removeQuestion: payload => dispatch(removeQuestion({ ...payload, questionnaireKey: QUESTIONNAIRE_KEY }))
});

const BloodTestConsultationNew = connect(mapStateToProps, mapDispatchToProps)(BloodTestConsultationUnconnected);

const immunisationWorkflowImprovementsPt1 = getFeatureToggles()[FeatureToggleNames.ImmunisationWorkflowImprovementsPt1];

export const BloodTestConsultation = immunisationWorkflowImprovementsPt1 ? BloodTestConsultationNew : BloodTestConsultationOld;
